import { abbreviateNumber } from '../../utils/data';
import { CapturedPlan } from './plan-telemetry';

// Renders the agent's own reasoning for the plan it just produced — captured live
// from the plan-submission tool call: every line's rationale and the networks the
// agent explicitly rejected (with reasons). The "why this plan" transparency
// artifact — the decision is inspectable, not opaque.
const dollars = (value: number) => `$${abbreviateNumber(value)}`;

interface DecisionTrailPanelProps {
  plan: CapturedPlan;
}

export const DecisionTrailPanel = ({ plan }: DecisionTrailPanelProps) => (
  <div className='elevate-trail'>
    <div className='elevate-trail-head'>
      <h3 className='elevate-trail-title'>Why this plan — decision trail</h3>
      <p className='elevate-trail-sub'>
        The agent&apos;s own reasoning, captured live from the plan it just produced — including the
        networks it considered and rejected.
      </p>
    </div>

    <StrategySection plan={plan} />
    <LineRationaleSection lineItems={plan.lineItems} />
    <ExcludedSection excludedOptions={plan.excludedOptions} />
  </div>
);

const StrategySection = ({ plan }: DecisionTrailPanelProps) => {
  const rows: { label: string; value: string }[] = [
    { label: 'Primary KPI', value: plan.primaryKpi },
    { label: 'Audience', value: plan.audienceStrategy },
    { label: 'Geo', value: plan.geoStrategy }
  ].filter(row => row.value);
  if (!plan.planSummary && rows.length === 0) return undefined;

  return (
    <section className='elevate-trail-section'>
      <h4 className='elevate-trail-section-title'>Strategy</h4>
      {plan.planSummary ? <p className='elevate-trail-summary'>{plan.planSummary}</p> : undefined}
      {rows.map(row => (
        <div key={row.label} className='elevate-trail-kv'>
          <span className='elevate-trail-kv-key'>{row.label}</span>
          <span className='elevate-trail-kv-val'>{row.value}</span>
        </div>
      ))}
    </section>
  );
};

const LineRationaleSection = ({ lineItems }: { lineItems: CapturedPlan['lineItems'] }) => {
  if (lineItems.length === 0) return undefined;
  return (
    <section className='elevate-trail-section'>
      <h4 className='elevate-trail-section-title'>Why each line</h4>
      <div className='elevate-trail-lines'>
        {lineItems.map((line, index) => (
          <div
            key={`${line.network}-${line.channel}-${line.budget}-${index}`}
            className='elevate-trail-line'
          >
            <div className='elevate-trail-line-head'>
              <span className='elevate-trail-line-network'>{line.network}</span>
              <span className='elevate-trail-line-meta'>
                {dollars(line.budget)} · {dollars(line.cpm)} CPM · {line.channel}
                {line.isDirectIo ? ' · direct IO' : ''}
              </span>
            </div>
            {line.audienceSegment ? (
              <p className='elevate-trail-line-audience'>
                <span className='elevate-trail-audience-label'>Audience</span>
                {line.audienceSegment}
              </p>
            ) : undefined}
            {line.rationale ? (
              <p className='elevate-trail-line-why'>{line.rationale}</p>
            ) : undefined}
          </div>
        ))}
      </div>
    </section>
  );
};

const ExcludedSection = ({
  excludedOptions
}: {
  excludedOptions: CapturedPlan['excludedOptions'];
}) => {
  if (excludedOptions.length === 0) return undefined;
  return (
    <section className='elevate-trail-section'>
      <h4 className='elevate-trail-section-title'>Considered &amp; rejected</h4>
      <div className='elevate-trail-excluded'>
        {excludedOptions.map(option => (
          <div key={option.name} className='elevate-trail-excluded-row'>
            <span className='elevate-trail-excluded-name'>{option.name}</span>
            <span className='elevate-trail-excluded-reason'>{option.reason}</span>
          </div>
        ))}
      </div>
    </section>
  );
};
